import { useState, useRef } from "react";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Upload, X } from "lucide-react";

/**
 * FileUpload — admin-only file uploader that stores a value in an input.
 * Returns a URL like `${API_BASE}/files/{storage_path}` that can be used directly in <img>, <video> or DB.
 *
 * Props:
 *   value: current URL (string)
 *   onChange: (newUrl: string) => void
 *   accept: "image/*" | "video/*" | "image/*,video/*"
 *   label: descriptive label
 *   testid: base data-testid
 */
export function FileUpload({ value, onChange, accept = "image/*", label = "Upload", testid = "upload" }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${API_BASE}/files/${data.storage_path}`;
      onChange(url);
      toast.success("Uploaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const isImage = value && /\.(jpe?g|png|gif|webp)$/i.test(value) || value?.includes("image");
  const isVideo = value && /\.(mp4|webm|mov)$/i.test(value) || accept.includes("video");

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid={`${testid}-file`}
        />
        <button
          type="button"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-red-50 text-red-700 hover:bg-red-100 transition disabled:opacity-60"
          data-testid={`${testid}-button`}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {busy ? "Uploading…" : label}
        </button>
        <input
          type="url"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="…or paste a URL"
          className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-red-500"
          data-testid={`${testid}-url`}
        />
        {value && (
          <button type="button" onClick={() => onChange("")} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100" data-testid={`${testid}-clear`}>
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {value && (
        <div className="rounded-xl border border-slate-200 p-2 bg-slate-50 max-w-xs">
          {isVideo && !isImage ? (
            <video src={value} className="w-full max-h-40 rounded-lg" controls />
          ) : (
            <img src={value} alt="preview" className="w-full max-h-40 rounded-lg object-cover" />
          )}
        </div>
      )}
    </div>
  );
}
